/** `ai.captor` — the capture-and-rescue rule. When a captor-tagged diver
 * starts a sortie and the field is clear (no beam, no carrier, no docked
 * wingman) it may drop a BEAM (a tethered, telegraphed entity: harmless
 * while it warns, then live) and creeps towards the nearest target. A target
 * standing in the live beam is CAPTURED, not hurt: it is parked (vitals
 * respawns it without a life lost), a CAPTIVE rides above the captor, and
 * the captor restarts its dive from the top, slowed. Destroy that carrier and
 * the captive is freed: it DOCKS beside the target as a seated wingman (its
 * own gun fires with the seat's button; its own hitbox widens the ship).
 * If the carrier leaves the bottom with it, the target forfeits a life.
 * Steering is intent (`steer` / `sortie-restart` signals to `ai.diver`). */
import type { JsonObject } from '../../json.js';
import { id, int, num, obj } from '../schemaBuilders.js';
import { defineModule, str, type ModuleCtx } from '../module.js';
import { addTag, overlaps, removeTag, type EntityV1 } from '../world.js';

export type CaptorConfig = {
  captorTag: string;
  /** Beam archetype: body.tether (below its emitter) + rule.telegraph. */
  beam: string;
  /** The beam's live tag (its telegraph's activeTag). */
  beamActiveTag: string;
  /** Captive/wingman archetype: body.tether (+ its own gun and hit points). */
  captive: string;
  targetTag: string;
  beamChance: number;
  trackSpeed: number;
  carryMul: number;
  parkTicks: number;
  dockOffset: number;
  rescueScore: number;
};

export const captor = defineModule<CaptorConfig>({
  id: 'ai.captor', version: 1, attach: 'global', role: 'behaviour',
  summary: 'Capture and rescue: a diving captor drops a telegraphed beam that captures (parks) a target; destroying the carrier docks the captive as a wingman; escape costs a life.',
  config: obj({ captorTag: id(), beam: id(), beamActiveTag: id(), captive: id(), targetTag: id(), beamChance: num(0, 1), trackSpeed: num(0, 20),
    carryMul: num(0.1, 2), parkTicks: int(1, 300), dockOffset: num(0.2, 4), rescueScore: int(0, 100000) }),
  requires: ['ai.diver', 'body.tether', 'rule.telegraph', 'core.vitals'], events: ['beam', 'capture', 'rescue', 'escape'],
  presentation: { beam: { cue: 'countdown' }, capture: { cue: 'fail', vfx: { effect: 'flash', palette: 3 } },
    rescue: { cue: 'powerup', vfx: { effect: 'popup', text: 'RESCUE', palette: 'slot' } }, escape: { cue: 'lose', vfx: { effect: 'shake' } } },
  proofs: ['beam-on-sortie', 'one-beam-at-a-time', 'telegraph-is-harmless', 'capture-parks-without-cost', 'carrier-restarts-slowed', 'rescue-docks-wingman',
    'wingman-fires-with-seat', 'escape-forfeits-life', 'no-beam-while-docked'],
  animStates: () => [],
  animStatesForTag: c => [{ tag: c.captorTag, states: ['action-1'] }],
  mapArchetypeRefs: (c, rename) => ({ ...c, beam: rename(c.beam), captive: rename(c.captive) }),
  check(c, context) {
    const errors: string[] = [];
    const beam = context.archetypes[c.beam]?.modules ?? [], captive = context.archetypes[c.captive]?.modules ?? [];
    if (!beam.includes('body.tether') || !beam.includes('rule.telegraph')) errors.push(`${c.beam} must use body.tether and rule.telegraph`);
    if (!captive.includes('body.tether')) errors.push(`${c.captive} must use body.tether`);
    if (!Object.values(context.archetypes).some(a => a.tags.includes(c.captorTag) && a.modules.includes('ai.diver'))) errors.push(`no ai.diver archetype is tagged ${c.captorTag}`);
    return errors;
  },
  initGlobal(ctx) { Object.assign(ctx.state, { carrier: '', victim: '', restart: '', beamed: {} }); },
  systems: {
    think(ctx) {
      const c = ctx.config, s = ctx.state;
      if (str(s, 'restart')) { ctx.signal('sortie-restart', ctx.entity(str(s, 'restart')) ?? null, null, {}); s.restart = ''; }
      const beams = ctx.entities({ archetype: c.beam, status: 'any' });
      const emitters = new Set(beams.map(b => str(ctx.peer(b, 'body.tether') ?? {}, 'parent')));
      const beamed = s.beamed as JsonObject;
      for (const boss of ctx.entities({ tag: c.captorTag, status: 'any' })) {
        if (emitters.has(boss.id)) addTag(boss, 'beaming'); else removeTag(boss, 'beaming');
        const dive = ctx.peer(boss, 'ai.diver');
        if (!dive || dive.mode !== 'dive' || boss.status !== 'active') continue;
        if (beamed[boss.id] !== dive.sorties) {
          beamed[boss.id] = dive.sorties ?? 0;
          const room = !str(s, 'carrier') && beams.length === 0 && nearestTarget(ctx, boss) !== undefined
            && !ctx.entities({ archetype: c.captive, status: 'any' }).some(w => w.tags.includes('docked'));
          if (room && ctx.random() < c.beamChance) {
            const beam = ctx.spawn(c.beam, { x: boss.x, y: boss.y, params: { parent: boss.id } });
            emitters.add(boss.id); addTag(boss, 'beaming');
            ctx.emit('beam', { x: beam.x, y: beam.y - beam.h / 2, kind: boss.archetype });
          }
        }
        const carrying = str(s, 'carrier') === boss.id;
        if (!carrying && !emitters.has(boss.id)) continue;
        const target = carrying ? ctx.entity(str(s, 'victim')) : nearestTarget(ctx, boss);
        if (target) ctx.signal('steer', boss, null, { x: target.x, rate: c.trackSpeed, mul: carrying ? c.carryMul : 1 });
      }
    },
    interact(ctx) {
      const c = ctx.config, s = ctx.state;
      const carrier = str(s, 'carrier');
      if (carrier && ctx.signals('sortie-end').some(sig => sig.target === carrier)) {
        const victim = ctx.entity(str(s, 'victim'));
        if (victim) ctx.signal('forfeit', victim, null, {});
        release(ctx, 'escape');
        return;
      }
      if (carrier) return;
      for (const beam of ctx.entities({ archetype: c.beam, status: 'active' })) {
        if (!beam.tags.includes(c.beamActiveTag)) continue;
        const boss = ctx.entity(str(ctx.peer(beam, 'body.tether') ?? {}, 'parent'));
        const victim = ctx.entities({ tag: c.targetTag, status: 'active' }).find(t => !t.tags.includes('invulnerable') && overlaps(ctx.world, beam, t));
        if (!boss || !victim) continue;
        Object.assign(s, { carrier: boss.id, victim: victim.id, restart: boss.id });
        ctx.spawn(c.captive, { x: boss.x, y: boss.y, params: { parent: boss.id } });
        ctx.signal('park', victim, boss, { ticks: c.parkTicks });
        ctx.despawn(beam, 'captured');
        removeTag(boss, 'beaming'); addTag(boss, 'carrying');
        ctx.emit('capture', { x: victim.x, y: victim.y - victim.h / 2, slot: victim.slot ?? 0 });
        return;
      }
    },
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      const carrier = str(s, 'carrier');
      if (!carrier) return;
      const defeat = ctx.signals('defeated').find(sig => sig.target === carrier);
      if (defeat) {
        const victim = ctx.entity(str(s, 'victim'));
        release(ctx, 'rescue');
        if (!victim || victim.slot === null) return;
        const side = victim.x < ctx.world.bounds.w / 2 ? 1 : -1;
        const wing = ctx.spawn(c.captive, { x: victim.x + side * c.dockOffset, y: victim.y, slot: victim.slot,
          status: victim.status === 'hidden' ? 'hidden' : 'active', params: { parent: victim.id, dx: side * c.dockOffset, dy: 0 } });
        addTag(wing, 'docked'); addTag(wing, 'ship');
        const slot = typeof defeat.data.slot === 'number' && defeat.data.slot >= 0 ? defeat.data.slot : victim.slot;
        ctx.addScore(slot, c.rescueScore);
        ctx.emit('rescue', { x: wing.x, y: wing.y - wing.h / 2, slot, points: c.rescueScore });
      } else if (!ctx.entity(carrier)) release(ctx, null);
    },
    present(ctx) {
      for (const boss of ctx.entities({ tag: ctx.config.captorTag })) if (boss.tags.includes('beaming') && boss.status === 'active') ctx.setAnim(boss, 'action-1');
    },
  },
  // A carrier is the rescue (shoot it). Without a wingman, a beam is the way
  // to earn one: being captured on purpose and shot free is the payoff.
  solverTargets(ctx, _e, player) {
    const carrier = ctx.entity(str(ctx.state, 'carrier'));
    if (carrier) return carrier.status === 'active' ? [{ x: carrier.x, y: player.y, weight: 12, label: 'rescue', press: true }] : [];
    if (ctx.entities({ archetype: ctx.config.captive, status: 'any' }).some(w => w.tags.includes('docked'))) return [];
    return ctx.entities({ archetype: ctx.config.beam }).map(beam => ({ x: beam.x, y: player.y, weight: 12, label: 'capture' }));
  },
});

function nearestTarget(ctx: ModuleCtx<CaptorConfig>, from: EntityV1): EntityV1 | undefined {
  let best: EntityV1 | undefined;
  for (const t of ctx.entities({ tag: ctx.config.targetTag, status: 'active' })) {
    if (!best || Math.abs(t.x - from.x) < Math.abs(best.x - from.x)) best = t;
  }
  return best;
}

/** End a carry: the carried captive goes with it (escape or rescue). */
function release(ctx: ModuleCtx<CaptorConfig>, outcome: 'escape' | 'rescue' | null): void {
  const s = ctx.state, carrier = ctx.entity(str(s, 'carrier'));
  for (const captive of ctx.entities({ archetype: ctx.config.captive, status: 'any' })) {
    if (!captive.tags.includes('docked')) ctx.despawn(captive, outcome ?? 'released');
  }
  if (carrier) removeTag(carrier, 'carrying');
  if (outcome === 'escape') ctx.emit('escape', { x: carrier?.x ?? 0, y: carrier ? carrier.y - carrier.h / 2 : 0 });
  Object.assign(s, { carrier: '', victim: '' });
}
